// src/components/EasyLoan.js
import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./EasyLoan.css";

const EasyLoan = () => {
  const formRef = useRef(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    amount: "",
    purpose: "",
    loanType: "",
  });
  const [appliedLoans, setAppliedLoans] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);

  const initialLoans = [
    {
      id: 1,
      title: "Small Business Loan",
      amount: "₹50,000 - ₹5,00,000",
      interest: "12%",
      term: "12-24 months",
      icon: "🏢",
      description: "Perfect for established businesses looking to expand operations or manage cash flow."
    },
    {
      id: 2,
      title: "Startup Loan",
      amount: "₹25,000 - ₹2,50,000",
      interest: "14%",
      term: "12-36 months",
      icon: "🚀",
      description: "Designed for new businesses with innovative ideas and growth potential."
    },
    {
      id: 3,
      title: "Equipment Financing",
      amount: "₹1,00,000 - ₹10,00,000",
      interest: "13%",
      term: "24-48 months",
      icon: "⚙️",
      description: "Finance your business equipment and machinery with flexible payment terms."
    },
    {
      id: 4,
      title: "Working Capital Loan",
      amount: "₹1,00,000 - ₹20,00,000",
      interest: "11%",
      term: "6-12 months",
      icon: "💰",
      description: "Quick access to funds for day-to-day business operations and expenses."
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const scrollToForm = (loan) => {
    setSelectedLoan(loan);
    setFormData(prev => ({
      ...prev,
      loanType: loan.title
    }));
    setShowForm(true);
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newLoan = {
      ...formData,
      id: Date.now(),
      status: "Pending",
      date: new Date().toLocaleDateString()
    };
    setAppliedLoans(prev => [...prev, newLoan]);
    setShowSuccess(true);
    setFormData({
      name: "",
      email: "",
      phone: "",
      amount: "",
      purpose: "",
      loanType: ""
    });
    setShowForm(false);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  const validateForm = () => {
    const { name, email, phone, amount, purpose, loanType } = formData;
    return name && email && phone && amount && purpose && loanType;
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="easyloan-container"
    >
      <motion.h1 
        initial={{ y: -20 }}
        animate={{ y: 0 }}
        className="h1-tag"
      >
        Business Loans
      </motion.h1>
      
      <motion.p 
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        className="welcome-text"
      >
        Choose the perfect loan option for your business needs
      </motion.p>

      <div className="loan-cards-container">
        {initialLoans.map((loan) => (
          <motion.div
            key={loan.id}
            className={`loan-card ${selectedLoan?.id === loan.id ? 'selected' : ''}`}
            whileHover={{ y: -5 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="loan-icon">{loan.icon}</div>
            <h3>{loan.title}</h3>
            <div className="loan-details">
              <p><strong>Amount:</strong> {loan.amount}</p>
              <p><strong>Interest Rate:</strong> {loan.interest}</p>
              <p><strong>Term:</strong> {loan.term}</p>
            </div>
            <p className="loan-description">{loan.description}</p>
            <motion.button
              className="apply-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToForm(loan)}
            >
              Apply Now
            </motion.button>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            ref={formRef}
            className="application-form-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <h2>Loan Application</h2>
            <form onSubmit={handleSubmit} className="application-form">
              <div className="form-group">
                <label htmlFor="loanType">Loan Type</label>
                <select
                  id="loanType"
                  name="loanType"
                  value={formData.loanType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a loan type</option>
                  {initialLoans.map(loan => (
                    <option key={loan.id} value={loan.title}>
                      {loan.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email address"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="amount">Loan Amount (₹)</label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter loan amount"
                />
              </div>

              <div className="form-group">
                <label htmlFor="purpose">Purpose of Loan</label>
                <textarea
                  id="purpose"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  required
                  placeholder="Describe how you plan to use the loan"
                  rows="4"
                />
              </div>

              <motion.button
                type="submit"
                className="submit-button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                disabled={!validateForm()}
              >
                Submit Application
              </motion.button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            className="success-message"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
          >
            <div className="success-content">
              <span className="success-icon">✓</span>
              <p>Application submitted successfully!</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {appliedLoans.length > 0 && (
        <motion.div
          className="applied-loans"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <h2>Your Applications</h2>
          <div className="applied-loans-grid">
            {appliedLoans.map((loan) => (
              <motion.div
                key={loan.id}
                className="applied-loan-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h3>{loan.loanType}</h3>
                <p><strong>Amount:</strong> ₹{loan.amount}</p>
                <p><strong>Status:</strong> {loan.status}</p>
                <p><strong>Date:</strong> {loan.date}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default EasyLoan;
